import React, { PureComponent } from 'react';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Map } from 'immutable';
import { routeMap } from 'router';
import { getDayDecline } from 'utils';
import config from 'config';
import LinkContainer from 'containers/LinkWrapperContainer';
import styles from './Header.module.scss';
import { ReactComponent as PhoneImage } from './images/phone.svg';
import { ReactComponent as CalendarImage } from './images/calendar.svg';
import { ReactComponent as ExitImage } from './images/exit.svg';
import { ReactComponent as LkipImage } from './images/work.svg';
import BannerList from 'components/BannerList';
import Wallet from '../Wallet';
import Storage from 'services/Storage';
import LogoutLinkContainer from 'modules/Authorization/containers/LogoutLinkContainer';
import UserInfo from '../UserInfo';
import REACT_CONFIG from 'REACT_CONFIG';
import { remotePaths } from 'router/routes';

const MESSAGE_TIMEOUT = 5 * 60 * 1000; // 5 minutes
const MOBILE_WIDTH = 768;

export default class Header extends PureComponent {
  static propTypes = {
    user: PropTypes.instanceOf(Map),
    messageCounter: PropTypes.instanceOf(Map),
    avatar: PropTypes.instanceOf(Map),
    isHomepage: PropTypes.bool.isRequired,
    showLogoutLink: PropTypes.bool,
    showAccessibilityLink: PropTypes.bool,
    lkIp: PropTypes.instanceOf(Map),
    showSearch: PropTypes.bool.isRequired,
    getLkIp: PropTypes.func.isRequired,
    getAvatar: PropTypes.func.isRequired,
    handlerSearch: PropTypes.func.isRequired,
    closeWriteOffMessage: PropTypes.func.isRequired,
    loadMessageCounter: PropTypes.func.isRequired,
  };

  counterInterval = null;

  state = {
    showReminderDelay: false,
    showLogoutLink: false,
    showAccessibilityLink: false,
    isCollapsed: true,
  };

  headerContainerRef = React.createRef();
  bannerListRef = React.createRef();

  componentDidMount() {
    const { avatar, lkIp, getAvatar, getLkIp } = this.props;
    // for component re-mount (example: home to page, page to home)
    // the first time the token is not present in the storage
    this.initMessageCounter();

    if (avatar.get('data') === null) {
      getAvatar();
    }

    if (!lkIp.get('data')) {
      getLkIp();
    }

    window.addEventListener('resize', this.reCalcHeaderOffset);
    setTimeout(this.reCalcHeaderOffset, 1000);
  }

  componentDidUpdate() {
    this.initMessageCounter();
    setTimeout(this.reCalcHeaderOffset, 0);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.reCalcHeaderOffset);
    this.clearMessageCounterInterval();
  }

  getPaymentReminder() {
    const { user } = this.props;
    const { isCollapsed } = this.state;
    const {
      data: { accrualIsPresent, hasDebts, daysLeft, hasData, sumToPay },
    } = user.toJS();
    const { showReminderDelay } = this.state;

    if ((!accrualIsPresent && !hasDebts) || !hasData || +sumToPay === 0) {
      return null;
    }

    if ((hasDebts && !accrualIsPresent) || (sumToPay > 0 && +daysLeft === 0)) {
      return (
        <div
          className={classnames('header-reminder', styles.reminder, {
            'header-reminder__active': showReminderDelay,
            [styles.reminderHidden]: !isCollapsed,
          })}
        >
          <div className="header-reminder_inner">
            <div
              className="header-reminder_delay"
              onClick={() => this.setState({ showReminderDelay: !showReminderDelay })}
            >
              <span className="link link_attention-orange">
                Срок оплаты истек, <br />
                оплатите сейчас
              </span>
            </div>

            {showReminderDelay && (
              <div className="header-reminder_more">
                Вам необходимо оплатить задолженность, иначе будут начислены пени
              </div>
            )}
          </div>
        </div>
      );
    }

    return (
      <div
        className={classnames('header-reminder', styles.reminder, {
          [styles.reminderHidden]: !isCollapsed,
        })}
      >
        <div className="header-reminder_inner">
          <div className="header-reminder_value">
            <span className="header-reminder_number">{daysLeft}</span>
          </div>
          <LinkContainer
            className="header-reminder_label first-letter-uppercase"
            href={routeMap.taxesPayment}
          >
            {getDayDecline(daysLeft)} до наступления
            <br />
            срока оплаты налога
          </LinkContainer>
        </div>
      </div>
    );
  }

  getUserInfo() {
    const { user, avatar } = this.props;

    if (this.noData()) {
      return null;
    }

    return (
      <div className="header-user" data-tour={'header__profile'}>
        {
          <UserInfo
            hasDebts={user.getIn(['data', 'hasDebts'])}
            avatar={avatar.get('data')}
            inn={user.getIn(['data', 'inn'])}
            userName={user.getIn(['data', 'name'])}
          />
        }
      </div>
    );
  }

  getMenuControls() {
    const controls = [
      {
        title: 'Контакты',
        href: remotePaths.contacts,
        imageComponent: PhoneImage,
      },
      {
        title: 'Календарь',
        href: remotePaths.homePage,
        imageComponent: CalendarImage,
      },
      {
        title: 'ЛКИП',
        href: routeMap.lkIp,
        imageComponent: LkipImage,
        isHidden: REACT_CONFIG.REACT_APP_HIDE_LKIP || !this.shouldRenderLkIp(),
      },
      {
        title: 'Выйти',
        component: () => (
          <LogoutLinkContainer title="Выход">
            <ExitImage />
            <span className={'replace_text'}>Выход</span>
          </LogoutLinkContainer>
        ),
        isDisabled: config.isInspectorArm,
      },
    ];

    return controls.filter(({ isHidden }) => !isHidden);
  }

  reCalcHeaderOffset = () => {
    try {
      const headerContainer = this.headerContainerRef;

      if (this.props.isHomepage && headerContainer) {
        const body = document.getElementsByTagName('body')[0];
        const rect = body ? body.getBoundingClientRect() : null;
        if (!rect || rect.width < MOBILE_WIDTH) {
          headerContainer.style.top = '0px';
        }
      }
    } catch (e) {
      //do-nothing
    }
  };

  loadMessageCounter = () => {
    const { loadMessageCounter } = this.props;
    loadMessageCounter();
  };

  startMessageCounterInterval = () => {
    this.loadMessageCounter();
    this.counterInterval = setInterval(this.loadMessageCounter, MESSAGE_TIMEOUT);
  };

  initMessageCounter = () => {
    const token = Storage.getItem(config.tokenItemName);
    if (!this.counterInterval && token) {
      this.startMessageCounterInterval();
    }
  };

  clearMessageCounterInterval = () => {
    clearInterval(this.counterInterval);
    this.counterInterval = null;
  };

  toggleMenu = () => {
    const { isCollapsed } = this.state;
    this.setState({ isCollapsed: !isCollapsed });
  };

  shouldRenderLkIp() {
    const { lkIp } = this.props;
    const lkIpData = lkIp.get('data');

    if (
      lkIp.get('loading') ||
      lkIp.get('error') ||
      !lkIpData ||
      !lkIpData.lkipEnable ||
      config.isInspectorArm
    ) {
      return null;
    }

    return true;
  }

  noData() {
    const { user } = this.props;
    const hasData = user.getIn(['data', 'hasData']);
    const { data } = user.toJS();

    return !isEmpty(data) && !hasData;
  }

  renderSearchIcon() {
    const { handlerSearch } = this.props;
    return (
      <a onClick={handlerSearch} className="header_link header_link__search" title="Поиск">
        <span className={'replace_text'}>Поиск</span>
      </a>
    );
  }

  renderWalletIcon() {
    const { user } = this.props;

    const balance = user.getIn(['data', 'enpSum'], 0);

    return (
      <a
        href={remotePaths.taxes}
        className={`${styles.walletLink}`}
        title="Кошелек"
        data-tour={'header__wallet'}
      >
        <span className={'replace_text'}>Кошелек</span>
        <Wallet value={balance} />
      </a>
    );
  }

  render() {
    const { isHomepage } = this.props;

    return (
      <div>
        {!config.enableNotificationsCenter && (
          <div ref={this.bannerListRef} key="banner-list">
            <BannerList />
          </div>
        )}
        <div
          ref={this.headerContainerRef}
          key="header-container"
          className={classnames('header-wrapper', {
            'header-wrapper__main': isHomepage,
          })}
        >
          <div
            className={classnames('header', 'content', {
              header__main: isHomepage,
              header__pages: !isHomepage,
            })}
          >
            <div className="header-title">
              <a
                className="header-title_icon"
                href={`${config.hrefToNewLK}/individual/main`}
                data-tour={'header__link_logo'}
              />
              <span className="replace_text">ФНС</span>
              <span className={styles.headerTitleText}>
                Личный <br /> кабинет <br /> налогоплательщика
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
